import {
  ChangeDetectorRef,
  Component,
  inject,
  model,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent,
  DateOptions,
  FormComponent,
  InputComponent,
  InputOptions,
  ModalComponent,
  ModalOptions,
} from '@shared/components';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DropdownComponent, DropdownOptions } from '@shared/components/dropdown/dropdown.component';
import { Product } from '@core/classes/other/product';
import { Package } from '@core/classes/other/package';
import { UnitIntake } from '@core/classes/other/unit-intake';
import { Coding } from '@core/classes/other/coding';
import { TranslateService, Translation } from '@ngx-translate/core';
import { APIService, DataService, ToastService } from '@core/services';
import { PatientService } from '@core/services/patient.service';
import { ATC } from '@core/classes/other/atc';
import { ActiveSubstance } from '@core/classes/other/active-substance';
import { DoseForm } from '@core/classes/other/dose-form';
import { SizeUnit } from '@core/classes/other/size-unit';
import { Route } from '@core/classes/other/route';
import { HTTPMethods, InputTypes, ModalTypes } from '@core/enums';
import { HttpHeaders } from '@angular/common/http';
import { MedicationSearchComponent } from '@shared/components/dropdown/search/medication-search/medication-search.component';
import {
  assets,
  FILTER_E_FROM_NUMERIC_INPUT,
  FILTER_NUMBERS_WITH_DECIMALS,
  FILTER_ONLY_NUMBERS,
  FILTER_ONLY_NUMBERS_WITH_DOT,
  FILTER_TEXT_FOR_TEXTAREA,
} from '@core/constants';
import { checkDate } from '@core/helpers';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Response, ResponseSingle } from '@core/classes/other/response';
import { Medication } from '@core/classes/tables/medication';
import { NgOptimizedImage } from '@angular/common';
import { environment } from '@env/environment';
import {
  TextAreaComponent,
  TextAreaOptions,
} from '@shared/components/text-area/text-area.component';
import { RadiosComponent, RadiosOptions } from '@shared/components/radios/radios.component';
import moment from 'moment';
import { ePrescription } from '@core/classes/tables/eprescription';

@UntilDestroy()
@Component({
  selector: 'ncp-add-eprescription',
  imports: [
    ButtonComponent,
    DateComponent,
    DropdownComponent,
    FormsModule,
    InputComponent,
    ModalComponent,
    NgOptimizedImage,
    ReactiveFormsModule,
    TextAreaComponent,
    RadiosComponent,
  ],
  templateUrl: './add-eprescription.component.html',
  styleUrl: './add-eprescription.component.scss',
})
export class AddEprescriptionComponent implements OnInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('mFrm') protected medicationFormEl?: NgForm;
  @ViewChild('addMedicationButton') public addMedicationButton!: ButtonComponent;

  @ViewChild('medicationDesc') public medicationDesc!: DropdownComponent<Product>;
  @ViewChild('quantityIn') public quantityIn!: InputComponent;
  @ViewChild('frequencyIn') public frequencyIn!: InputComponent;
  @ViewChild('durationIn') public durationIn!: InputComponent;
  @ViewChild('packageDesc') public packageDesc!: DropdownComponent<Package>;
  @ViewChild('noPackages') public noPackages!: InputComponent;
  @ViewChild('unitIntakeDesc') public unitIntakeDesc!: DropdownComponent<UnitIntake>;
  @ViewChild('timeUnitDesc') public timeUnitDesc!: DropdownComponent<Coding>;
  @ViewChild('durationUnitDesc') public durationUnitDesc!: DropdownComponent<Coding>;
  @ViewChild('onSetDate') public onSetDate!: DateComponent;
  @ViewChild('instPatients') public instPatients!: TextAreaComponent;
  @ViewChild('instPharmacists') public instPharmacists!: TextAreaComponent;
  @ViewChild('substitution') public substitution!: RadiosComponent;

  @ViewChild('medicationTemplate') public medicationTemplate!: TemplateRef<Product>;

  @ViewChild('addMedicationButton') getSPbtn = () => {
    return this.addMedicationButton;
  };

  private readonly translate = inject(TranslateService);
  protected translations: Translation = this.translate.instant('patient.ps.medication');
  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  protected allInfo = model<
    | {
        pbn: Product;
        atc: ATC;
        pas: ActiveSubstance[];
        pdf: DoseForm;
        ppu: SizeUnit;
        prt: Route;
        ppd: Package;
        pdu: Coding[];
      }
    | undefined
  >();

  private _params = {};
  protected form: FormGroup = new FormGroup(
    {
      coding_id: new FormControl<number | string | undefined>(undefined, [Validators.required]),
      package_id: new FormControl<number | string | undefined>(
        { value: undefined, disabled: true },
        [Validators.required]
      ),
      package_num: new FormControl<number | string | undefined>(
        { value: undefined, disabled: true },
        [Validators.required]
      ),
      substitution: new FormControl<string | undefined>({ value: undefined, disabled: true }, [
        Validators.required,
      ]),
      unitsIntake_id: new FormControl<number | string | undefined>(
        { value: undefined, disabled: true },
        [Validators.required]
      ),
      frequencyUnits_id: new FormControl<number | string | undefined>(
        { value: undefined, disabled: true },
        [Validators.required]
      ),
      durationUnits_id: new FormControl<number | string | undefined>(
        { value: undefined, disabled: true },
        [Validators.required]
      ),
      quantity: new FormControl<string>({ value: '', disabled: true }, [
        Validators.required,
        Validators.min(1),
      ]),
      frequency: new FormControl<string>({ value: '', disabled: true }, [
        Validators.required,
        Validators.min(1),
      ]),
      duration: new FormControl<string>({ value: '', disabled: true }, [
        Validators.required,
        Validators.min(1),
      ]),
      onSetDate: new FormControl<string>({ value: '', disabled: true }, [
        Validators.required,
        Validators.required,
      ]),
      instructions_to_patient: new FormControl<string | undefined>(
        { value: undefined, disabled: true },
        []
      ),
      instructions_to_dispencer: new FormControl<string | undefined>(
        { value: undefined, disabled: true },
        []
      ),
    },
    {
      validators: [this.formValidator()],
    }
  );

  public btnMOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'ndaBtn',
      tooltip: 'add_eprescription',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: 'add_eprescription',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: 'lg',
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {
        this.onFormReset();
      },
    })
  );

  protected medicationDescDDOptions = signal<DropdownOptions<Product | ATC>>(
    new DropdownOptions<Product | ATC>({
      id: 'medicationDescription',
      name: 'medicationDescription',
      placeholder: 'medicines_search',
      label: {
        id: 'medication_desc_label',
        text: 'medicines_search',
      },
      api: {
        brandName: {
          path: '/get-product',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: '',
          },
        },
        activeSubstance: {
          path: '/get-products_substance',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: '',
          },
        },
        atcCode: {
          path: '/get-products_atc',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: '',
          },
        },
      },
      dataId: 'drugid',
      required: true,
      searchComponent: MedicationSearchComponent,
      formControl: this.form.get('coding_id') as FormControl,
    })
  );

  protected packageDescDDOptions = signal<DropdownOptions<Package>>(
    new DropdownOptions<Package>({
      id: 'packageDescription',
      name: 'packageDescription',
      placeholder: 'package_description',
      label: {
        id: 'package_desc_label',
        text: 'package_description',
      },
      dataId: 'packnr',
      required: true,
      searchComponent: null,
      formControl: this.form.get('package_id') as FormControl,
      disabled: true,
    })
  );

  protected numPackagesInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'numPackages',
      name: 'numPackages',
      label: {
        id: 'numberOfPackages',
        text: 'numberOfPackages',
      },
      type: InputTypes.number,
      pattern: FILTER_ONLY_NUMBERS,
      formControl: this.form.get('package_num') as FormControl,
      onKeyDown: (event, value) => {
        ['e', 'E', '+', '-', /U+0384/].includes(event.key) && event.preventDefault();
      },
      required: true,
      disabled: true,
      min: 1,
      step: 1,
    })
  );

  protected substitutionRadiosOptions = signal<RadiosOptions>(
    new RadiosOptions({
      id: 'medicationRadios',
      options: [
        {
          value: 0,
          label: 'yes',
        },
        {
          value: 1,
          label: 'no',
        },
      ],
      formControl: this.form.get('substitution') as FormControl,
      // onInput: (value) => {
      //   console.log('tes ' + value);
      //   (this.form.get('substitution') as FormControl).setValue(value === 0 ? 'yes' : 'no');
      // },
      required: true,
      disabled: true,
    })
  );

  protected quantityInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'quantity',
      name: 'quantity',
      label: {
        id: 'quantity_label',
        text: 'quantity',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.form.get('quantity') as FormControl,
      onKeyDown: (event, value) => {
        ['e', 'E', '+', '-', /U+0384/].includes(event.key) && event.preventDefault();
      },
      required: true,
      disabled: true,
      min: 1,
    })
  );

  protected unitsPerIntakeDDOptions = signal<DropdownOptions<UnitIntake>>(
    new DropdownOptions<UnitIntake>({
      id: 'unitsPerIntake',
      name: 'unitsPerIntake',
      placeholder: 'units_intake_description',
      label: {
        id: 'unitsPerIntake_label',
        text: 'units_intake_description',
      },
      searchComponent: null,
      formControl: this.form.get('unitsIntake_id') as FormControl,
      disabled: true,
    })
  );

  protected frequencyInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'frequencyOfUse',
      name: 'frequencyOfUse',
      label: {
        id: 'frequencyOfUse_label',
        text: 'frequency_of_use',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.form.get('frequency') as FormControl,
      onKeyDown: (event, value) => {
        ['e', 'E', '+', '-', /U+0384/].includes(event.key) && event.preventDefault();
      },
      required: true,
      disabled: true,
      min: 1,
    })
  );

  protected frequencyUnitsDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'timeUnit',
      name: 'timeUnit',
      placeholder: 'time_unit_description',
      label: {
        id: 'timeUnit_label',
        text: 'time_unit_description',
      },
      searchComponent: null,
      formControl: this.form.get('frequencyUnits_id') as FormControl,
      disabled: true,
    })
  );

  protected durationInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'treatmentDuration',
      name: 'treatmentDuration',
      label: {
        id: 'treatmentDuration_label',
        text: 'treatment_duration',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.form.get('duration') as FormControl,
      onKeyDown: (event, value) => {
        ['e', 'E', '+', '-', /U+0384/].includes(event.key) && event.preventDefault();
      },
      required: true,
      disabled: true,
      min: 1,
    })
  );

  protected durationUnitsDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'durationUnit',
      name: 'durationUnit',
      placeholder: 'time_unit_description',
      label: {
        id: 'durationUnit_label',
        text: 'time_unit_description',
      },
      searchComponent: null,
      formControl: this.form.get('durationUnits_id') as FormControl,
      disabled: true,
    })
  );

  protected onSetDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'onSetDate',
      label: {
        text: 'on_set',
      },
      name: 'onSetDate',
      initialValue: '',
      formControl: this.form.get('onSetDate') as FormControl,
      required: true,
      disabled: true,
      minDate: moment().format('YYYY-MM-DD'),
    })
  );

  protected patientInstructionsTAOptions = signal<TextAreaOptions>(
    new TextAreaOptions({
      id: 'instructions_patient',
      label: {
        text: 'instructions_patient',
      },
      name: 'instructions_patient',
      formControl: this.form.get('instructions_to_patient') as FormControl,
      initialValue: '',
      pattern: FILTER_TEXT_FOR_TEXTAREA,
      rows: 1,
      disabled: true,
    })
  );

  protected pharmacistInstructionsTAOptions = signal<TextAreaOptions>(
    new TextAreaOptions({
      id: 'instructions_pharmacist',
      label: {
        text: 'instructions_pharmacist',
      },
      name: 'instructions_pharmacist',
      formControl: this.form.get('instructions_to_dispencer') as FormControl,
      initialValue: '',
      pattern: FILTER_TEXT_FOR_TEXTAREA,
      rows: 1,
      disabled: true,
    })
  );

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let codingId = (control as FormGroup).controls['coding_id'];
      let substitution = (control as FormGroup).controls['substitution'];
      let packageId = (control as FormGroup).controls['package_id'];
      let noPackages = (control as FormGroup).controls['package_num'];
      let quantity = (control as FormGroup).controls['quantity'];
      let unitsIntakeId = (control as FormGroup).controls['unitsIntake_id'];
      let frequency = (control as FormGroup).controls['frequency'];
      let frequencyUnitsId = (control as FormGroup).controls['frequencyUnits_id'];
      let duration = (control as FormGroup).controls['duration'];
      let durationUnitsId = (control as FormGroup).controls['durationUnits_id'];
      let onSetDate = (control as FormGroup).controls['onSetDate'];
      let instPatients = (control as FormGroup).controls['instructions_to_patient'];
      let instPharmacists = (control as FormGroup).controls['instructions_to_dispencer'];

      let patientDOB = moment(
        this.patientService.data?.dob,
        ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'],
        true
      );
      let today = moment().format('YYYY/MM/DD');

      if (codingId.value === undefined || codingId.value === null) {
        codingId.setErrors({ m_codingIdInvalid: true });
        errors['m_codingIdInvalid'] = true;
      }

      if (substitution.enabled && (substitution.value === undefined || substitution.value === null)) {
        substitution.setErrors({ m_substitutionInvalid: true });
        errors['m_substitutionInvalid'] = true;
      }

      if (packageId.enabled && (packageId.value === undefined || packageId.value === null)) {
        packageId.setErrors({ m_packageIdInvalid: true });
        errors['m_packageIdInvalid'] = true;
      }

      if (noPackages.enabled &&
        noPackages.hasValidator(Validators.required) &&
        (noPackages.value === undefined || noPackages.value === null)
      ) {
        noPackages.setErrors({ m_noPackagesInvalid: true });
        errors['m_noPackagesInvalid'] = true;
      }

      if (
        quantity.enabled &&
        quantity.hasValidator(Validators.required) &&
        (quantity.value === undefined || quantity.value === null || quantity.value === '')
      ) {
        quantity.setErrors({ m_quantityInvalid: true });
        errors['m_quantityInvalid'] = true;
      }

      if (
        unitsIntakeId.enabled &&
        unitsIntakeId.hasValidator(Validators.required) &&
        (unitsIntakeId.value === undefined || unitsIntakeId.value === null)
      ) {
        unitsIntakeId.setErrors({ m_unitsIntakeInvalid: true });
        errors['m_unitsIntakeInvalid'] = true;
      }

      if (
        frequency.enabled &&
        frequency.hasValidator(Validators.required) &&
        (frequency.value === undefined || frequency.value === null || frequency.value === '')
      ) {
        frequency.setErrors({ m_frequencyInvalid: true });
        errors['m_frequencyInvalid'] = true;
      }

      if (
        frequencyUnitsId.enabled &&
        frequencyUnitsId.hasValidator(Validators.required) &&
        (frequencyUnitsId.value === undefined || frequencyUnitsId.value === null)
      ) {
        frequencyUnitsId.setErrors({ m_frequencyUnitsInvalid: true });
        errors['m_frequencyUnitsInvalid'] = true;
      }

      if (
        duration.enabled &&
        duration.hasValidator(Validators.required) &&
        (duration.value === undefined || duration.value === null || duration.value === '')
      ) {
        duration.setErrors({ m_durationInvalid: true });
        errors['m_durationInvalid'] = true;
      }

      if (
        durationUnitsId.enabled &&
        durationUnitsId.hasValidator(Validators.required) &&
        (durationUnitsId.value === undefined || durationUnitsId.value === null)
      ) {
        durationUnitsId.setErrors({ m_durationUnitsInvalid: true });
        errors['m_durationUnitsInvalid'] = true;
      }

      if (onSetDate.enabled && (!checkDate(onSetDate.value).isValid())) {
        onSetDate.setErrors({ m_onSetDateInvalid: true });
        errors['m_onSetDateInvalid'] = true;
      } else if(onSetDate.enabled && (checkDate(onSetDate.value).isValid())) {
        if (checkDate(onSetDate.value).isBefore(today)) {
          onSetDate.setErrors({ m_onSetDateAfterDOB: true });
          errors['m_onSetDateAfterToday'] = true;
        } else {
          onSetDate.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  ngOnInit() {
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('patient.ps.medication');
    });

    this.btnMOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;

      let medicationDescOptions = this.medicationDescDDOptions();
      medicationDescOptions.itemTemplate = this.medicationTemplate;
      medicationDescOptions.onSelect = this.onSelectMedication;
      medicationDescOptions.onClear = this.onFormReset;
      this.medicationDescDDOptions.set(medicationDescOptions);
      this.medicationDesc.detectChanges();

      let unitIntakeOptions = this.unitsPerIntakeDDOptions();
      unitIntakeOptions.onSelect = this.onSelectIntakeUnit;
      this.unitsPerIntakeDDOptions.set(unitIntakeOptions);
      this.unitIntakeDesc.detectChanges();

      let timeUnitOptions = this.frequencyUnitsDDOptions();
      timeUnitOptions.onSelect = this.onSelectTimeUnit;
      this.frequencyUnitsDDOptions.set(timeUnitOptions);
      this.timeUnitDesc.detectChanges();

      let durationOptions = this.durationUnitsDDOptions();
      durationOptions.onSelect = this.onSelectDurationUnit;
      this.durationUnitsDDOptions.set(durationOptions);
      this.durationUnitDesc.detectChanges();

      this.addMedicationButton.blur();
      this.mod.open();
    };

    this.modOptions().onSave = this.onSave;

    this.form.valueChanges.subscribe((value) => {
      if (this.form.disabled) {
        return;
      }

      if (value?.coding_id) {
        if (this.form.get('package_id')?.disabled) {
          this.packageDesc?.enable();
        }
        if (this.form.get('substitution')?.disabled) {
          this.substitution?.enable();
        }
        if (this.form.get('package_num')?.disabled) {
          this.noPackages?.enable();
        }
        if (this.form.get('quantity')?.disabled) {
          this.quantityIn?.enable();
        }
        if (this.form.get('frequency')?.disabled) {
          this.frequencyIn?.enable();
        }
        if (this.form.get('duration')?.disabled) {
          this.durationIn?.enable();
        }
      }

      // QUANTITY
      if (
        value.quantity &&
        Number(value.quantity) > 0 &&
        this.form.get('quantity')?.valid &&
        this.form.get('unitsIntake_id')?.disabled
      ) {
        this.unitIntakeDesc?.enable();
        this.unitsPerIntakeDDOptions().required = true;
        //this.form.get('unitsIntake_id')?.setValidators([Validators.required]);
      }

      if (
        ((!value.quantity && this.form.get('quantity')?.valid) ||
          (value.quantity <= 0 && !this.form.get('quantity')?.valid)) &&
        this.form.get('unitsIntake_id')?.enabled
      ) {
        this.unitIntakeDesc?.disable();
        this.unitsPerIntakeDDOptions().required = false;
        //this.form.get('unitsIntake_id')?.clearValidators();
      }

      // FREQUENCY
      if (
        value.frequency &&
        Number(value.frequency) > 0 &&
        this.form.get('frequency')?.valid &&
        this.form.get('frequencyUnits_id')?.disabled
      ) {
        this.timeUnitDesc?.enable();
        this.frequencyUnitsDDOptions().required = true;
        //this.form.get('frequencyUnits_id')?.setValidators([Validators.required]);
      }

      if (
        ((!value.frequency && this.form.get('frequency')?.valid) ||
          (value.frequency <= 0 && !this.form.get('frequency')?.valid)) &&
        this.form.get('frequencyUnits_id')?.enabled
      ) {
        this.timeUnitDesc?.disable();
        this.frequencyUnitsDDOptions().required = false;
        //this.form.get('frequencyUnits_id')?.clearValidators();
      }

      // DURATION
      if (
        value.duration &&
        Number(value.duration) > 0 &&
        this.form.get('duration')?.valid &&
        this.form.get('durationUnits_id')?.disabled
      ) {
        this.durationUnitDesc?.enable();
        this.durationUnitsDDOptions().required = true;
        //this.form.get('durationUnits_id')?.setValidators([Validators.required]);
      }

      if (
        ((!value.duration && this.form.get('duration')?.valid) ||
          (value.duration <= 0 && !this.form.get('duration')?.valid)) &&
        this.form.get('durationUnits_id')?.enabled
      ) {
        this.durationUnitDesc?.disable();
        this.durationUnitsDDOptions().required = false;
        //this.form.get('durationUnits_id')?.clearValidators();
      }
    });
  }

  onFormReset = (clearMedication: boolean = true) => {
    console.log('on reset form');
    this.form.reset();
    if (clearMedication) {
      this.medicationDesc.reset();
    }

    this.noPackages.reset();

    this.unitIntakeDesc.reset();
    this.timeUnitDesc.reset();
    this.durationUnitDesc.reset();
    this.packageDesc.reset();

    this.quantityIn?.reset();
    this.frequencyIn?.reset();
    this.durationIn?.reset();

    this.onSetDate.reset();

    this.instPatients.reset();
    this.instPharmacists.reset();

    this.substitution.reset();

    this.modOptions().errors = [];
  };

  ngAfterViewInit(): void {}

  onSelectMedication = (data?: Product) => {
    if (data) {
      this.form.disable();
      this.medicationDescDDOptions().isLoading = true;
      this.apiService
        .callMany([
          {
            path: '/get-atc_code',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              atc: data.atc,
            },
            name: 'atc',
          },
          {
            path: '/get-product_active_substance',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'pas',
          },
          {
            path: '/get-product_form',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'pdf',
          },
          {
            path: '/get-product_pack_unit',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'ppu',
          },
          {
            path: '/get-product_route',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'prt',
          },
          {
            path: '/get-product_pack_desc',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'ppd',
          },
          {
            path: '/get-product_duration_unit',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {},
            name: 'pdu',
          },
        ])
        ?.subscribe((res: any) => {
          if (
            res &&
            res['atc'] &&
            res['pas'] &&
            res['pdf'] &&
            res['ppu'] &&
            res['prt'] &&
            res['ppd'] &&
            res['pdu']
          ) {
            let packageData = (res['ppd'] as Response<Package>).data!;
            packageData.map((item) => {
              item.description = item.packageDescription;
            });
            let allData = {
              pbn: data,
              atc: (res['atc'] as Response<ATC>).data?.[0]!,
              pas: (res['pas'] as Response<ActiveSubstance>).data!,
              pdf: (res['pdf'] as Response<DoseForm>).data?.[0]!,
              ppu: (res['ppu'] as Response<SizeUnit>).data?.[0]!,
              prt: (res['prt'] as Response<Route>).data?.[0]!,
              pdu: (res['pdu'] as Response<Coding>).data!,
              ppd: packageData,
            };
            this.allInfo.set(allData);

            this.packageDesc.filteredData = packageData;
            this.packageDesc.detectChanges();

            const unitsPerIntakeList: Array<UnitIntake> = [
              {
                description: this.translations.not_applicable,
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: allData.ppu.packageSizeUnit,
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'dose(s)',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'drop(s)',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'gram',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'international unit',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'kilo international unit',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'liters',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'microequivalent',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'microgram',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'milliequivalent',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'milligram',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'milliliter',
                coding_id: -1,
                drugid: data.drugid,
              },
              {
                description: 'million international unit',
                coding_id: -1,
                drugid: data.drugid,
              },
            ];

            const timeUnits = [
              {
                epSOS_OID: '1.3.6.1.4.1.12559.11.10.1.3.1.42.16',
                coding_id: 22977,
                code: '/d',
                value: 'hour',
                description: this.translations.hour,
              },
              {
                epSOS_OID: '1.3.6.1.4.1.12559.11.10.1.3.1.42.16',
                coding_id: 22905,
                code: '/d',
                value: 'day',
                description: this.translations.day,
              },
              {
                epSOS_OID: '1.3.6.1.4.1.12559.11.10.1.3.1.42.16',
                coding_id: 23371,
                code: '/h',
                value: 'week',
                description: this.translations.week,
              },
              {
                epSOS_OID: '1.3.6.1.4.1.12559.11.10.1.3.1.42.16',
                coding_id: 23161,
                code: '/h',
                value: 'month',
                description: this.translations.month,
              },
            ];

            this.allInfo()?.pdu?.map((item: Coding) => {
              item['value'] = item.description;
              item.description = this.translations[item.description!];
            });

            this.unitIntakeDesc.filteredData = unitsPerIntakeList;
            this.unitIntakeDesc.detectChanges();

            this.timeUnitDesc.filteredData = timeUnits;
            this.timeUnitDesc.detectChanges();

            this.durationUnitDesc.filteredData = this.allInfo()?.pdu || [];
            this.durationUnitDesc.detectChanges();

            this.form.enable();
            this.medicationDescDDOptions().isLoading = false;
          }
        });
    } else {
      this.allInfo.set(undefined);
      this.packageDesc.filteredData = [];
      this.packageDesc.detectChanges();
    }
  };

  onSelectIntakeUnit = (data?: UnitIntake) => {};

  onSelectTimeUnit = (data?: Coding) => {};

  onSelectDurationUnit = (data?: Coding) => {};

  onSave = () => {
    this.medicationFormEl?.ngSubmit.emit();
  };

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      if (this.form.controls[control].enabled) {
        this.form.controls[control].markAsTouched();
      }
    });

    return true;
  };

  private calculateEndDateForApi(
    onset?: string,
    duration?: string | number,
    unit?: string
  ): string | null {
    if (!onset || !duration || !unit) {
      return null;
    }

    const d = new Date(onset);
    const amount = Number(duration);

    if (unit === 'day') d.setDate(d.getDate() + amount);
    if (unit === 'week') d.setDate(d.getDate() + amount * 7);
    if (unit === 'month') d.setMonth(d.getMonth() + amount);
    if (unit === 'year') d.setFullYear(d.getFullYear() + amount);

    return d.toISOString().substring(0, 10);
  }

  onFormSubmit = () => {
    if (this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      productName: this.allInfo()?.pbn.productName,
      substitution: (this.form.get('substitution') as FormControl).value,
      atc: this.allInfo()?.atc.code,
      strength: this.allInfo()?.pbn.strength,
      pharmaceutical_dose_form: this.allInfo()?.pdf.form,
      units_per_intake: this.unitsPerIntakeDDOptions().value?.description,
      frequency_of_intake: (this.form.get('frequency') as FormControl).value,
      frequency_of_intake_unit_id: this.frequencyUnitsDDOptions().value?.coding_id,
      route_of_administration: this.allInfo()?.prt.route,
      route_of_administration_id: this.allInfo()?.prt['coding_id'] || null,
      quantity: (this.form.get('quantity') as FormControl).value,
      packageDescription: this.packageDescDDOptions().value!.description,
      package_num: (this.form.get('package_num') as FormControl).value,
      date_of_onset: (this.form.get('onSetDate') as FormControl).value,
      end_date: this.calculateEndDateForApi(
        (this.form.get('onSetDate') as FormControl).value,
        (this.form.get('duration') as FormControl).value,
        this.durationUnitsDDOptions().value ? this.durationUnitsDDOptions().value!['value'] : ''
      ),
      duration: (this.form.get('duration') as FormControl).value,
      duration_unit_id: this.durationUnitsDDOptions().value?.coding_id,
      instructions_to_dispencer: (this.form.get('instructions_to_dispencer') as FormControl).value,
      instructions_to_patient: (this.form.get('instructions_to_patient') as FormControl).value,
      packageSize: this.packageDescDDOptions().value!.packageSize,
      packnr: this.packageDescDDOptions().value!.packnr,
      status: 1,
      version_id: this.patientService.version!.new,

      //units_per_intake_coding_id: this.unitsPerIntakeDDOptions().value?.coding_id,
      //searchName: this.medicationDesc.searchTerms['medication'],
      //drugid: this.allInfo()?.pbn.drugid,
      //activeSubstances: this.allInfo()?.pas,
      //funit: this.frequencyUnitsDDOptions().value ? this.frequencyUnitsDDOptions().value!['value'] : "",
      //duration_unit: this.durationUnitsDDOptions().value ? this.durationUnitsDDOptions().value!['value'] : "",
    };

    this.apiService
      .call({
        path: '/prescription',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        type: HTTPMethods.POST,
        body: body,
      })
      ?.subscribe((res: ResponseSingle<ePrescription>) => {
        if (res && res.status?.toString() === '1') {
          const dateOfExpiry = this.calculateExpiryTimestamp(
            res.data?.date_of_onset,
            res.data?.duration,
            res.data?.duration_unit
          );

          let data = this.data()?.tblOptions().data;
          let new_data = {
            version_id: -1,
            date_of_expiry: res.data?.expiry_date,
            prescription_id: `${res.data?.prescription_id}`,
            issue_date: this.formatDate(res.data?.date_of_issue),
            expiry_date: this.calculateExpiryDate(
              res.data?.date_of_onset,
              res.data?.duration,
              res.data?.duration_unit
            ),
            description: `(${res.data?.active_ingredient_id}) ${res.data?.active_ingredient} ${res.data?.strength}`,
            status: Number(res.data?.status ?? 1),
            status_desc: this.getStatusDescription(Number(res.data?.status)),
            is_open: false,
          };
          let allData = {
            ...res.data,
            ...new_data,
          };
          data.unshift(allData);
          //data.unshift(body);
          if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
            this.data()!.tbl!.tableData! = data;
          }
          this.mod.close();
        }
      });
  };

  private getStatusDescription(status: number): string {
    switch (status) {
      case 0:
        return 'Expired';

      case 1:
        return 'Active';

      case 2:
        return 'Deleted';

      case 3:
        return 'Dispensed';

      default:
        return 'Unknown';
    }
  }

  private formatDate(date?: string): string {
    if (!date) {
      return '';
    }

    const d = new Date(date);

    if (isNaN(d.getTime())) {
      return date;
    }

    return d.toLocaleDateString('en-GB');
  }

  private calculateExpiryDate(onset?: string, duration?: string, unit?: string): string {
    const timestamp = this.calculateExpiryTimestamp(onset, duration, unit);

    if (!timestamp) {
      return '';
    }

    return this.formatDate(new Date(timestamp).toISOString());
  }

  private calculateExpiryTimestamp(
    onset?: string,
    duration?: string,
    unit?: string
  ): number | null {
    if (!onset) {
      return null;
    }

    const d = new Date(onset);
    const amount = Number(duration || 0);

    if (isNaN(d.getTime())) {
      return null;
    }

    if (unit === 'day') {
      d.setDate(d.getDate() + amount);
    }

    if (unit === 'week') {
      d.setDate(d.getDate() + amount * 7);
    }

    if (unit === 'month') {
      d.setMonth(d.getMonth() + amount);
    }

    if (unit === 'year') {
      d.setFullYear(d.getFullYear() + amount);
    }

    return d.getTime();
  }

  protected readonly assets = assets;
  protected readonly environment = environment;
}
